import { Check } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { BotaoOlhinho } from '../../valores-ocultos/ValoresOcultos';
import { CabecalhoPagina } from './ui';

/**
 * O topo do Dashboard: só o "Ver como", do super admin. A data e a saudação
 * foram para o Feed em 30/09/2026, quando ele virou a tela de entrada do
 * produto — o Dashboard ficou só com os números (`Saudacao`, abaixo).
 *
 * A frase motivacional saiu daqui em 10/09/2026: o Início abre com o resumo do
 * agente, que é informação do dia, e uma citação logo acima competia com ele
 * pelo mesmo olhar. No Hub ela continua.
 *
 * O "Ver como" troca o ESCOPO DOS DADOS, não o papel: os cards passam a
 * mostrar a carteira do vendedor escolhido, mas o resumo do agente (que é do
 * usuário logado) sai de cena e entra o texto determinístico — está explicado
 * no ResumoDoAgente.
 *
 * A seleção é MÚLTIPLA: dá para comparar dois especialistas ou olhar o time de
 * um gerente inteiro sem trocar um por vez. Nenhum marcado = a casa toda.
 * Trocar o <Select> por uma lista com caixas foi necessário porque um select
 * nativo não seleciona vários sem virar um controle estranho no teclado.
 *
 * O olhinho dos valores (09/10/2026) mora à esquerda desta mesma linha, para
 * todo mundo: o Dashboard não tem título, e este é o topo da tela. Fica longe
 * do "Ver como" porque os dois usam um olho e fazem coisas diferentes.
 */

const saudacao = () => {
  const h = new Date().getHours();
  if (h < 12) return 'Bom dia';
  if (h < 18) return 'Boa tarde';
  return 'Boa noite';
};

const dataDeHoje = () => {
  const texto = new Date().toLocaleDateString('pt-BR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });
  return texto.charAt(0).toUpperCase() + texto.slice(1);
};

/** Data e saudação pela hora — o topo do Feed. */
export const Saudacao = ({ firstName }: { firstName: string }) => (
  <div className="min-w-0">
    <p className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-ouro">{dataDeHoje()}</p>
    <h1 className="mt-1 truncate text-[24px] font-bold leading-tight tracking-[-0.02em] text-white sm:text-[26px]">
      {saudacao()}
      {firstName ? `, ${firstName}` : ''}
    </h1>
  </div>
);

export const CabecalhoInicio = ({
  isSuperAdmin,
  vendedores,
  viewAsCrmUserIds,
  onViewAsChange,
}: {
  isSuperAdmin: boolean;
  vendedores: { id: number; name: string; gerenteId?: number | null }[];
  /** Vazio = a casa toda. */
  viewAsCrmUserIds: number[];
  onViewAsChange: (ids: number[]) => void;
}) => {
  const escolhidos = new Set(viewAsCrmUserIds);

  // Só é gerente quem tem alguém apontando para ele. Assim o atalho "time de"
  // nunca aparece vazio.
  const gerentes = vendedores.filter((g) => vendedores.some((v) => v.gerenteId === g.id));

  const alternar = (id: number) => {
    const novo = new Set(escolhidos);
    if (novo.has(id)) novo.delete(id);
    else novo.add(id);
    onViewAsChange([...novo]);
  };

  const marcarTime = (gerenteId: number) => {
    const time = vendedores.filter((v) => v.gerenteId === gerenteId || v.id === gerenteId).map((v) => v.id);
    const jaTodos = time.every((id) => escolhidos.has(id));
    const novo = new Set(escolhidos);
    // Clicar de novo no mesmo time desmarca: é o mesmo botão para ligar e desligar.
    for (const id of time) {
      if (jaTodos) novo.delete(id);
      else novo.add(id);
    }
    onViewAsChange([...novo]);
  };

  const rotulo =
    escolhidos.size === 0
      ? 'Todos os vendedores'
      : escolhidos.size === 1
        ? (vendedores.find((v) => v.id === viewAsCrmUserIds[0])?.name ?? '1 vendedor')
        : `${escolhidos.size} vendedores`;

  // Premium North (19/10/2026): o topo vira o cabeçalho de página com o degradê,
  // com o título "Dashboard". O olhinho continua colado no título e o "Ver como"
  // do super admin continua à direita — os mesmos dois controles de antes.
  const verComo = isSuperAdmin ? (
        <div className="flex shrink-0 items-center gap-2">
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="h-8 w-52 justify-between bg-card text-xs font-normal">
                <span className="truncate">{rotulo}</span>
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-72 p-0">
              <div className="flex items-center justify-between border-b px-3 py-2">
                <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Ver como
                </span>
                <button
                  type="button"
                  className="text-xs text-primary hover:underline disabled:opacity-40"
                  disabled={escolhidos.size === 0}
                  onClick={() => onViewAsChange([])}
                >
                  Todos
                </button>
              </div>

              {gerentes.length > 0 && (
                <div className="border-b px-3 py-2">
                  <p className="mb-1.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                    Por gerente
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {gerentes.map((g) => (
                      <button
                        key={g.id}
                        type="button"
                        className="rounded bg-muted px-2 py-1 text-xs hover:bg-muted/70"
                        onClick={() => marcarTime(g.id)}
                      >
                        {g.name.split(' ')[0]}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="max-h-64 overflow-y-auto py-1">
                {vendedores.map((v) => (
                  <button
                    key={v.id}
                    type="button"
                    role="option"
                    aria-selected={escolhidos.has(v.id)}
                    className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-muted/50"
                    onClick={() => alternar(v.id)}
                  >
                    <span className="flex h-4 w-4 shrink-0 items-center justify-center rounded border">
                      {escolhidos.has(v.id) && <Check className="h-3 w-3" />}
                    </span>
                    <span className="truncate">{v.name}</span>
                  </button>
                ))}
              </div>
            </PopoverContent>
          </Popover>
        </div>
  ) : undefined;

  return <CabecalhoPagina titulo="Dashboard" aoLadoDoTitulo={<BotaoOlhinho />} acoes={verComo} />;
};
